import type { ItemSpec } from './itemSpec';
import { sortSpecsByCuratedGroups } from './movingItemGroupOrder';

/** 방 타입 코드. 앱의 `RoomTypeValue` 가 그대로 들어온다. */
type RoomTypeValue = string;
type Spec = ItemSpec;


/** FlowDocument item-picker input 과 호환되는 섹션 (title 은 string 고정) */
export interface MovingItemPickerFlowItem {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  flow?: string;
  /** "전체 품목" 섹션에서 상단(주로 쓰는 항목)과 중복되는 카드 → 회색 처리 */
  dimmed?: boolean;
  /** 이 짐이 도달 가능한 서비스 모드. 여기 없는 모드에서는 카드가 흐려진다. */
  enabledModeIds?: string[];
}

export interface MovingItemPickerFlowSection {
  id: string;
  title: string;
  items: MovingItemPickerFlowItem[];
}

export function specMatchesRoomType(spec: Spec, roomType: RoomTypeValue): boolean {
  const tags = spec.roomTypes ?? [];
  return tags.length > 0 && tags.includes(roomType);
}

/**
 * 스펙 타입을 열어 둔다. 앱은 자기 `Spec`(속성 필수)을, 프리뷰는 API 응답 형태를
 * 넘기는데 둘 다 여기서 읽는 필드는 같다. 고정하면 `toItem` 이 서로 안 맞는다.
 */
export interface BuildMovingItemPickerSectionsParams<S extends Spec = Spec> {
  specs: S[];
  currentRoomType?: RoomTypeValue;
  toItem: (spec: S) => MovingItemPickerFlowItem;
}

/**
 * 현재 기록 중인 방 타입 기준으로 ItemPicker 섹션을 만든다.
 *
 * - 항목은 항상 큐레이션 그룹 순서(`sortSpecsByCuratedGroups`)로 정렬해 유사 짐이
 *   인접하게 보이도록 한다 (세탁기·건조기 / 책장·선반 등).
 * - roomTypes 에 currentRoomType 이 포함된 spec → "주로 쓰는 항목"(상단)
 * - "전체 품목"(하단) 은 모든 항목을 동일한 고정 순서로 표시하며, 상단에 올라간
 *   항목은 빠지지 않고 `dimmed` 로 회색 처리된다. 방마다 하단 배열 위치가 바뀌지
 *   않아 숙련 시 빠르게 짐을 찾을 수 있다.
 * - currentRoomType 이 없거나(운반 경로 등) primary 가 비어 있으면 단일 "전체 품목"
 *   섹션만 노출한다 (프로덕션 roomTypes 미설정 fallback).
 */
export function buildMovingItemPickerSections<S extends Spec>({
  specs,
  currentRoomType,
  toItem,
}: BuildMovingItemPickerSectionsParams<S>): MovingItemPickerFlowSection[] {
  const orderedSpecs = sortSpecsByCuratedGroups(specs);
  const items = orderedSpecs.map(toItem);

  if (!currentRoomType) {
    return [{ id: 'all', title: '전체 품목', items }];
  }

  const specById = new Map(orderedSpecs.map((s) => [s.id, s]));
  const primary: MovingItemPickerFlowItem[] = [];

  for (const item of items) {
    const spec = specById.get(item.id);
    if (spec && specMatchesRoomType(spec, currentRoomType)) {
      primary.push(item);
    }
  }

  if (primary.length === 0) {
    return [{ id: 'all', title: '전체 품목', items }];
  }

  // 모든 항목이 primary 면 하단 "전체 품목" 은 회색 중복일 뿐이라 노이즈가 되므로
  // 상단 섹션만 노출한다.
  if (primary.length === items.length) {
    return [{ id: 'primary', title: '주로 쓰는 항목', items: primary }];
  }

  // 하단 "전체 품목" — 모든 항목을 고정 순서로 그대로 표시한다.
  // (과거에는 상단 primary 중복 항목을 dimmed 로 회색 처리했으나, 이제 흐림 처리는
  //  서비스 모드에서 "불가" 를 뜻하므로 중복 표시용 dimming 은 제거한다 — 정상 항목이
  //  불가처럼 보이는 혼선 방지.)
  return [
    { id: 'primary', title: '주로 쓰는 항목', items: primary },
    { id: 'all', title: '전체 품목', items },
  ];
}
